import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { StudentService } from '../../_services/student/student.service';
import { ThemeService } from '../../_helpers/theming-engine/theme.service';
import { SuccessModalHelperService } from '../../_helpers/success-modal-helper/success-modal-helper.service';
import { GrantStudentAccessResponse } from '../../_models/grant-student-access/grant-student-access-response.model';
import { EmojiHelperService } from '../../_helpers/emojis/emoji-helper.service';
import { FeatureHelperService } from '../../_helpers/feature-helper/feature-helper.service';
import { StudentResetPinResponse } from '../../_models/student-login/student-reset-pin-response.model';
import { ErrorModalHelperService } from '../../_helpers/error-modal-helper/error-modal-helper.service';

@Component({
    selector: 'generate-student-access',
    templateUrl: './generate-student-access.component.html',
    styleUrls: ['./generate-student-access.component.css'],
    standalone: false
})
export class GenerateStudentAccessComponent implements OnInit, OnChanges {
  @Input() studentId: number;
  @Input() showHeader: boolean;
  @Input() studentName: string;
  @Input() showWebsiteLinkButton?: boolean;
  @Input() showSignUpEmailButton?: boolean;
  
  @ViewChild("pin1", { static: false }) pin1: ElementRef;
  @ViewChild("pin2", { static: false }) pin2: ElementRef;
  @ViewChild("pin3", { static: false }) pin3: ElementRef;
  @ViewChild("pin4", { static: false }) pin4: ElementRef;

  public showGenerateErrorMessage: boolean = false;
  // Proactive readiness (bug 37289): checked when the learner loads, so the "fix in Orbit" cue shows
  // up front rather than after a wasted button press. accessReady gates the generate buttons.
  public accessChecking: boolean = false;
  public accessReady: boolean = true;
  public accessNotReadyReason: string = "";
  // Deep link to the learner's order in Orbit ("Open in Orbit"), supplied by the API. Bug 37289.
  public orbitLearnerUrl: string = "";
  public showHeaderText: boolean;
  public generatedSuccessfully: boolean = false;
  public isLinkResponse: boolean = false;
  public isPinResponse: boolean = false;
  public isNumericPin: boolean;
  public studentLoginUrl: string = "";
  public showEmailRequiredError: boolean = false;
  public showEmailInvalidError: boolean = false;
  public emailAddress: string = "";

  public hasEmailStudentAccessLinkPermission: boolean = false;


  public showWebsiteLink: boolean = true;
  public showSignUpEmail: boolean = true;

  constructor(
    public themeService: ThemeService,
    private studentService: StudentService,
    private successModalHelper: SuccessModalHelperService,
    private emojiHelper: EmojiHelperService,
    private featureHelperService: FeatureHelperService,
    private errorModalHelper: ErrorModalHelperService
  ) {
  }

  ngOnInit() {
    setTimeout(() => {
      let featureList = new Array<string>();
      featureList.push("EmailStudentAccessLink");

      let currentUserFeatures = this.featureHelperService.checkFeaturePermissions(featureList);

      this.hasEmailStudentAccessLinkPermission = currentUserFeatures.filter(f => f.feature === "EmailStudentAccessLink")[0].enabled;
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.showWebsiteLink = this.showWebsiteLinkButton == null ? true : this.showWebsiteLinkButton;
    this.showSignUpEmail = this.showSignUpEmailButton == null ? true : this.showSignUpEmailButton;
    if (changes['studentId'] && this.studentId > 0) {
      this.checkAccessReadiness();
    }
  }

  // Checks up front whether the learner can be granted access (needs a first name and date of birth,
  // which now come from Orbit). Drives the "fix in Orbit" cue before any button is pressed. Bug 37289.
  private checkAccessReadiness(): void {
    this.accessChecking = true;
    this.accessReady = true;
    this.accessNotReadyReason = "";
    this.studentService.selectStudent(this.studentId).subscribe((student: any) => {
      this.accessChecking = false;
      this.orbitLearnerUrl = student?.orbitLearnerUrl || "";
      const detail = student?.studentDetailItem;
      const firstName = (detail?.firstName || "").trim();
      if (!firstName) {
        this.accessReady = false;
        this.accessNotReadyReason = "This learner isn't fully set up yet.";
      } else if (!detail?.dateOfBirth) {
        this.accessReady = false;
        this.accessNotReadyReason = "No date of birth is recorded for this learner.";
      } else {
        this.accessReady = true;
      }
    }, () => {
      // If the check itself fails, don't block — the on-click path still guards generation.
      this.accessChecking = false;
      this.accessReady = true;
    });
  }

  public generateAccess(type: string): void {
    
    this.generatedSuccessfully = false;
    this.showGenerateErrorMessage = false;

    let accessType: number = 0;
    // 1 = Guid
    // 2 = Pin
    // 3 = Emoji
    switch (type) {
      case "link":
        accessType = 1;
        break;
      case "number":
        accessType = 2;
        break;
      case "emoji":
        accessType = 3;
        break;
    }
    this.studentService.generateAccess(this.studentId, accessType).subscribe((grantAccessResponse: GrantStudentAccessResponse) => {

      this.generatedSuccessfully = true;
      this.studentLoginUrl = grantAccessResponse.accessUrl + (grantAccessResponse.accessGuid != null ? "?guid=" + grantAccessResponse.accessGuid : "");

      if (type == 'link') {
        this.isLinkResponse = true;
        this.isPinResponse = false;
      } else {
        this.isLinkResponse = false;
        this.isPinResponse = true;

        setTimeout(() => {
          this.clearPinBoxes();

          this.isNumericPin = grantAccessResponse.isNumeric;
          if (grantAccessResponse.isNumeric) {
            this.pin1.nativeElement.innerText = grantAccessResponse.pinOne;
            this.pin2.nativeElement.innerText = grantAccessResponse.pinTwo;
            this.pin3.nativeElement.innerText = grantAccessResponse.pinThree;
            this.pin4.nativeElement.innerText = grantAccessResponse.pinFour;
          } else {
            this.pin1.nativeElement.classList.add(this.emojiHelper.getEmojiClass(grantAccessResponse.pinOne));
            this.pin2.nativeElement.classList.add(this.emojiHelper.getEmojiClass(grantAccessResponse.pinTwo));
            this.pin3.nativeElement.classList.add(this.emojiHelper.getEmojiClass(grantAccessResponse.pinThree));
            this.pin4.nativeElement.classList.add(this.emojiHelper.getEmojiClass(grantAccessResponse.pinFour));
          }
        }, 0);
        
      }
    }, error => {
      this.showGenerateErrorMessage = true;
      console.error(error);
    });
  }

  public emailLink(): void {
    if (this.emailAddress == "") {
      this.showEmailRequiredError = true;
    } else if (!/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/g.test(this.emailAddress)) {
      this.showEmailRequiredError = false;
      this.showEmailInvalidError = true;
    } else {
      this.showEmailRequiredError = false;
      this.showEmailInvalidError = false;

      // The API says whether the email went (learner.weblink.email can be off), so only a sent email is reported as sent (40300).
      this.studentService.emailLoginLink(this.studentId, this.studentLoginUrl, this.emailAddress).subscribe((response: StudentResetPinResponse) => {
        if (response?.successful) {
          this.successModalHelper.show(response.message);
        } else {
          this.errorModalHelper.show(response?.message || "No email was sent.", { isValidationError: false });
        }
      }, error => {
        this.errorModalHelper.show(error.message, error);
      });
    }
  }

  public copyLink(inputElement): void{
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = inputElement.innerText;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
  }

  private clearPinBoxes(): void {
    this.pin1.nativeElement.innerText = "";
    let pin1List: Array<string> = new Array<string>();
    pin1List.push(...this.pin1.nativeElement.classList);
    let pin1ClassList = pin1List.slice(0);
    pin1ClassList.forEach((className: string) => {
      if (className.indexOf("emoji") != -1 && className != "pin-box-emoji") {
        this.pin1.nativeElement.classList.remove(className);
      }
    });

    this.pin2.nativeElement.innerText = "";
    let pin2List: Array<string> = new Array<string>();
    pin2List.push(...this.pin2.nativeElement.classList);
    let pin2ClassList = pin2List.slice(0);
    pin2ClassList.forEach((className: string) => {
      if (className.indexOf("emoji") != -1 && className != "pin-box-emoji") {
        this.pin2.nativeElement.classList.remove(className);
      }
    });

    this.pin3.nativeElement.innerText = "";
    let pin3List: Array<string> = new Array<string>();
    pin3List.push(...this.pin3.nativeElement.classList);
    let pin3ClassList = pin3List.slice(0);
    pin3ClassList.forEach((className: string) => {
      if (className.indexOf("emoji") != -1 && className != "pin-box-emoji") {
        this.pin3.nativeElement.classList.remove(className);
      }
    });

    this.pin4.nativeElement.innerText = "";
    let pin4List: Array<string> = new Array<string>();
    pin4List.push(...this.pin4.nativeElement.classList);
    let pin4ClassList = pin4List.slice(0);
    pin4ClassList.forEach((className: string) => {
      if (className.indexOf("emoji") != -1 && className != "pin-box-emoji") {
        this.pin4.nativeElement.classList.remove(className);
      }
    });
  }

  public sendSignUpEmail(): void {
    this.studentService.sendSignUpEmail(this.studentId).subscribe((response: StudentResetPinResponse) => {
      if (response.successful) {
        this.successModalHelper.show(response.message);
      } else {
        this.errorModalHelper.show(response.message, {isValidationError: false});
      }
    }, error => {
      this.errorModalHelper.show("An error has occurred. Please try again or contact your administrator.", error);
    });
  }

}
